import { Component, EventEmitter, Input, Output, ViewEncapsulation, inject } from '@angular/core';
import { ProductService } from '../../generic/service/product.service';
import { SCREEN_SIZE } from '../ivy-carousel/screen-size.enum';
import { RenderProductImageComponent } from '../../generic/component/products/render.product.image';
import { CarouselComponent } from '../ivy-carousel/carousel.component';

/**
 * Renders a carousel of the original image with an array of additional images
 */
@Component({
    selector: 'render-product-thumbnail-carousel',
    templateUrl: 'render.product.thumbnail.carousel.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./render.product.thumbnail.carousel.component.scss'],
    standalone: true,
    imports: [CarouselComponent, RenderProductImageComponent],
})
export class RenderProductThumbnailCarouselComponent {
    private productService = inject(ProductService);

    prefix: string = 'is-';

    thumbnailSliders = [
        {
            id: SCREEN_SIZE.XS,
            value: 3,
            css: `d-block d-sm-none`,
        },
        {
            id: SCREEN_SIZE.SM,
            value: 3,
            css: `d-none d-sm-block d-md-none`,
        },
        {
            id: SCREEN_SIZE.MD,
            value: 4,
            css: `d-none d-md-block d-lg-none`,
        },
        {
            id: SCREEN_SIZE.LG,
            value: 6,
            css: `d-none d-lg-block d-xl-none`,
        },
        {
            id: SCREEN_SIZE.XL,
            value: 6,
            css: `d-none d-xl-block`,
        },
    ];

    @Input() additionalImages: string[];
    @Input() productTitle: string;
    imageFormat: number = 490;

    @Output() gotoSlide = new EventEmitter<number>();

    /**
     * Removes the first character of the string if it starts with a '!' (bang)
     * ! = indication first picture.
     *
     * @param image
     * @private
     */
    private static removeBangFromImage(image: string): string {
        if (image) {
            return image.startsWith('!') ? image.substring(1) : image;
        }

        return '/images/fallback-product.png';
    }

    /**
     * Sorts the array so that the image with a '!' is first. Then converts the array by checking
     * if the string starts with '!' and removes that '!' so that it returns the image id.
     * The first spread seperator ([...]) makes sure we make a copy of the original array so that
     * we don't override the original array and change the immutable original array.
     */
    additionalImagesInCorrectOrder(): string[] {
        return [...this.additionalImages]
            .sort()
            .map(image =>
                RenderProductThumbnailCarouselComponent.removeBangFromImage(
                    image,
                ),
            );
    }

    getImageAltTitle(index: number, isThumbnail: boolean) {
        let productImageText = $localize`:@@product.image.alt.suffix:product image`;

        if (isThumbnail) {
            productImageText = `${productImageText} thumbnail`;
        }

        return this.productService.trimWhiteSpaceForProductDescriptions(
            `${this.productTitle} - ${productImageText} ${index}`,
        );
    }

    goToSlide(index: number): void {
        this.gotoSlide.emit(index);
    }

    hasAdditionalImages(): boolean {
        return this.additionalImages.length <= 1;
    }
}
